/**
 * Socket.IO for the agent side.
 *
 * The handshake carries the access token *and* the appId — the server proves
 * membership before joining `app:{appId}`, so switching tenants means a fresh
 * connection, never a client-side room swap.
 */
import { io } from 'socket.io-client';
import { CLIENT_EVENTS, SERVER_EVENTS } from '@superchat/shared/events';
import { API_BASE, loadTokens, forceRefresh } from './api.js';

export function createAgentSocket({ appId, onEvent, onStatus }) {
  let socket = null;
  let subscribed = null;
  let typingTimer = null;
  let refreshing = false;

  function connect() {
    if (!appId) return;

    socket = io(API_BASE || window.location.origin, {
      path: '/socket.io',
      // A callback, not a literal: socket.io calls it on every connection
      // *and* every reconnection attempt, so the handshake always carries the
      // current access token rather than the one that was valid at mount.
      auth: (cb) => cb({ token: loadTokens()?.accessToken, appId }),
      transports: ['websocket', 'polling'],
      reconnectionAttempts: Infinity,
      reconnectionDelay: 700,
      reconnectionDelayMax: 6000,
    });

    socket.on('connect', () => {
      onStatus?.('connected');
      // Rooms are per-socket; re-join whatever thread is open after a drop.
      if (subscribed) subscribe(subscribed);
    });

    socket.on('disconnect', () => onStatus?.('disconnected'));

    socket.on('connect_error', async (err) => {
      if (err.message !== 'unauthorized') {
        onStatus?.('error');
        return;
      }

      // The handshake was rejected because the access token has expired. This
      // is not an HTTP 401, so nothing else refreshes it — do it here, then
      // reconnect. The auth callback above picks up the new token.
      onStatus?.('unauthorized');
      if (refreshing) return;
      refreshing = true;
      try {
        await forceRefresh();
        socket?.connect();
      } catch {
        // Refresh failed: the session is genuinely over and forceRefresh has
        // already dropped the app back to the login screen.
      } finally {
        refreshing = false;
      }
    });

    for (const event of Object.values(SERVER_EVENTS)) {
      socket.on(event, (payload) => onEvent?.(event, payload));
    }
  }

  function subscribe(conversationId) {
    subscribed = conversationId;
    if (!socket?.connected || !conversationId) return;
    socket.emit(CLIENT_EVENTS.CONVERSATION_SUBSCRIBE, { conversationId });
  }

  function unsubscribe(conversationId) {
    if (subscribed === conversationId) subscribed = null;
    socket?.emit(CLIENT_EVENTS.CONVERSATION_UNSUBSCRIBE, { conversationId });
  }

  function setTyping(conversationId, isTyping) {
    if (!socket?.connected || !conversationId) return;
    socket.emit(CLIENT_EVENTS.TYPING, { conversationId, isTyping });
    clearTimeout(typingTimer);
    if (isTyping) {
      typingTimer = setTimeout(
        () => socket?.emit(CLIENT_EVENTS.TYPING, { conversationId, isTyping: false }),
        3000
      );
    }
  }

  function disconnect() {
    clearTimeout(typingTimer);
    socket?.removeAllListeners();
    socket?.disconnect();
    socket = null;
  }

  connect();

  return { subscribe, unsubscribe, setTyping, disconnect };
}

export default createAgentSocket;
